import React from 'react';
import Seo from '../components/Seo';
import { Shield, Eye, Lock, Globe, FileText, AlertCircle } from 'lucide-react';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Seo 
        title="Datenschutzerklärung - Fliesen Jarek | DSGVO konform"
        description="Datenschutzerklärung von Fliesen Jarek nach DSGVO. Informationen zur Verarbeitung personenbezogener Daten."
        keywords="Datenschutz, DSGVO, Fliesen Jarek, Datenschutzerklärung, personenbezogene Daten"
      />
      
      <div className="max-w-4xl mx-auto px-6 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-light text-stone-800 mb-6">
            Datenschutzerklärung
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Informationen zur Verarbeitung Ihrer personenbezogenen Daten gemäß Art. 13, 14 DSGVO
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          {/* Verantwortlicher */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Verantwortlicher</h2>
            </div>
            <div className="text-stone-600 leading-relaxed">
              <p className="mb-4">
                Verantwortlicher im Sinne der Datenschutz-Grundverordnung und anderer nationaler Datenschutzgesetze der Mitgliedsstaaten sowie sonstiger datenschutzrechtlicher Bestimmungen ist:
              </p>
              <div className="bg-stone-50 p-6 rounded-lg">
                <p className="font-medium text-stone-800 mb-2">Jarek Jozwiak</p>
                <p>Fliesenlegermeister</p>
                <p>Cuxhaven, Deutschland</p>
                <p>Telefon: 0151 54610847</p>
                <p>E-Mail: jarek.j79@web.de</p>
              </div>
            </div>
          </div>

          {/* Überblick über die Datenverarbeitung */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Eye className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Überblick über die Datenverarbeitung</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-6">
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Arten der verarbeiteten Daten</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Kontaktdaten (z. B. E-Mail, Telefonnummer)</li>
                  <li>Inhaltsdaten (z. B. Texteingaben, Fotografien, Videos)</li>
                  <li>Nutzungsdaten (z. B. besuchte Webseiten, Interesse an Inhalten, Zugriffszeiten)</li>
                  <li>Meta-/Kommunikationsdaten (z. B. Geräte-Informationen, IP-Adressen)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Kategorien betroffener Personen</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Kommunikationspartner</li>
                  <li>Nutzer (z. B. Webseitenbesucher, Nutzer von Onlinediensten)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Zwecke der Verarbeitung</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Kontaktanfragen und Kommunikation</li>
                  <li>Erbringung vertraglicher Leistungen und Kundenservice</li>
                  <li>Sicherheitsmaßnahmen</li>
                  <li>Verwaltung und Beantwortung von Anfragen</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Rechtsgrundlagen */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Rechtsgrundlagen der Verarbeitung</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                <strong className="text-stone-800">Art. 6 Abs. 1 lit. a DSGVO</strong> - Einwilligung: Die Verarbeitung ist rechtmäßig, wenn die betroffene Person ihre Einwilligung zu der Verarbeitung der sie betreffenden personenbezogenen Daten für einen oder mehrere bestimmte Zwecke gegeben hat.
              </p>
              <p>
                <strong className="text-stone-800">Art. 6 Abs. 1 lit. b DSGVO</strong> - Vertragserfüllung: Die Verarbeitung ist für die Erfüllung eines Vertrags, dessen Vertragspartei die betroffene Person ist, oder zur Durchführung vorvertraglicher Maßnahmen erforderlich.
              </p>
              <p>
                <strong className="text-stone-800">Art. 6 Abs. 1 lit. f DSGVO</strong> - Berechtigte Interessen: Die Verarbeitung ist zur Wahrung der berechtigten Interessen des Verantwortlichen oder eines Dritten erforderlich, sofern nicht die Interessen oder Grundrechte und Grundfreiheiten der betroffenen Person überwiegen.
              </p>
            </div>
          </div>

          {/* Datenverarbeitung im Detail */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Globe className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Datenverarbeitung im Detail</h2>
            </div>
            <div className="space-y-6">
              {/* Kontaktaufnahme */}
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Kontaktaufnahme</h3>
                <div className="text-stone-600 leading-relaxed space-y-2">
                  <p><strong>Verarbeitete Daten:</strong> Name, E-Mail-Adresse, Telefonnummer, Nachrichteninhalt</p>
                  <p><strong>Zweck:</strong> Bearbeitung von Kontaktanfragen und Kommunikation</p>
                  <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung), Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)</p>
                  <p><strong>Speicherdauer:</strong> Bis zur vollständigen Bearbeitung der Anfrage, danach Löschung nach 3 Jahren</p>
                </div>
              </div>

              {/* Website-Nutzung */}
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Website-Nutzung</h3>
                <div className="text-stone-600 leading-relaxed space-y-2">
                  <p><strong>Verarbeitete Daten:</strong> IP-Adresse, Browser-Informationen, Zugriffszeiten, besuchte Seiten</p>
                  <p><strong>Zweck:</strong> Bereitstellung der Website, Sicherheit, technische Administration</p>
                  <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)</p>
                  <p><strong>Speicherdauer:</strong> Server-Logs werden nach 7 Tagen automatisch gelöscht</p>
                </div>
              </div>

              {/* Netlify Forms */}
              <div>
                <h3 className="font-semibold text-stone-800 mb-3">Kontaktformular (Netlify Forms)</h3>
                <div className="text-stone-600 leading-relaxed space-y-2">
                  <p><strong>Verarbeitete Daten:</strong> Formulardaten (Name, E-Mail, Telefon, Nachricht)</p>
                  <p><strong>Zweck:</strong> Verarbeitung und Weiterleitung von Kontaktanfragen</p>
                  <p><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung)</p>
                  <p><strong>Drittanbieter:</strong> Netlify Inc., USA (adequacy decision)</p>
                  <p><strong>Speicherdauer:</strong> 30 Tage bei Netlify, danach lokale Archivierung für 3 Jahre</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ihre Rechte */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Lock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Ihre Rechte als betroffene Person</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Auskunftsrecht (Art. 15 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, Auskunft über die von uns verarbeiteten personenbezogenen Daten zu verlangen.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Berichtigungsrecht (Art. 16 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, die Berichtigung unrichtiger oder Vervollständigung unvollständiger Daten zu verlangen.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Löschungsrecht (Art. 17 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu verlangen.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Einschränkungsrecht (Art. 18 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, die Einschränkung der Verarbeitung zu verlangen.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Datenübertragbarkeit (Art. 20 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen Format zu erhalten.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">Widerspruchsrecht (Art. 21 DSGVO)</h3>
                  <p className="text-sm">Sie haben das Recht, gegen die Verarbeitung Ihrer Daten Widerspruch einzulegen.</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                <p className="text-sm">
                  <strong>Zur Ausübung Ihrer Rechte wenden Sie sich an:</strong><br />
                  E-Mail: jarek.j79@web.de<br />
                  Telefon: 0151 54610847
                </p>
              </div>
            </div>
          </div>

          {/* Beschwerderecht */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <AlertCircle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Beschwerderecht</h2>
            </div>
            <div className="text-stone-600 leading-relaxed">
              <p className="mb-4">
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über unsere Verarbeitung personenbezogener Daten zu beschweren.
              </p>
              <div className="bg-stone-50 p-6 rounded-lg">
                <p className="font-medium text-stone-800 mb-2">Zuständige Aufsichtsbehörde:</p>
                <p>Die Landesbeauftragte für den Datenschutz Niedersachsen</p>
                <p>Prinzenstraße 5</p>
                <p>30159 Hannover</p>
                <p>Telefon: 0511 120-4500</p>
                <p>E-Mail: poststelle@lfd.niedersachsen.de</p>
                <p>Website: <a href="https://lfd.niedersachsen.de" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">lfd.niedersachsen.de</a></p>
              </div>
            </div>
          </div>

          {/* Datensicherheit */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Lock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Datensicherheit</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird.
              </p>
              <p>
                Unsere Website und Systeme sind durch technische und organisatorische Maßnahmen gegen Verlust, Zerstörung, Zugriff, Veränderung oder Verbreitung Ihrer Daten durch unbefugte Personen geschützt.
              </p>
              <div className="bg-primary-50 p-4 rounded-lg">
                <h3 className="font-semibold text-stone-800 mb-2">Sicherheitsmaßnahmen:</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>SSL/TLS-Verschlüsselung für alle Datenübertragungen</li>
                  <li>Regelmäßige Sicherheitsupdates</li>
                  <li>Zugriffsbeschränkungen auf personenbezogene Daten</li>
                  <li>Sichere Speicherung und Archivierung</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Änderungen der Datenschutzerklärung */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">Änderungen der Datenschutzerklärung</h2>
            </div>
            <div className="text-stone-600 leading-relaxed">
              <p>
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-16 pt-8 border-t border-stone-200">
          <p className="text-sm text-stone-500">
            Stand: {new Date().toLocaleDateString('de-DE', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DatenschutzPage; 